import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import BasicForm from '~/admin/shared/forms/BasicForm/BasicForm';
import SubmitGroup from '~/admin/shared/forms/SubmitGroup';
// @ts-expect-error component requires TS conversion
import TextFieldGroup from '~/admin/shared/forms/TextFieldGroup';
import { SendSignInLinkDocument } from '~/libs/gql/mutations/eat/sendSignInLinkMutation.generated';
import { useEatSession } from '../../hooks/useEatSession';
import { useReturnTo, withReturnTo } from '../../hooks/useReturnTo';
import { SignInLayout } from '../SignInLayout';
import { SocialSignInButtons } from '../SocialSignInButtons';

const useStyles = makeStyles(theme => ({
  divider: {
    '&::before, &::after': {
      backgroundColor: theme.palette.gray.main,
      content: '""',
      flexGrow: 1,
      height: 1,
    },
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  expired: {
    backgroundColor: theme.palette.surface.main,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  legal: {
    '& a': {
      color: theme.palette.red.main,
    },
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

type SignInFormValues = {
  email?: string
}

export const SignInPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const { search } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const returnTo = useReturnTo();
  const { loading, user } = useEatSession();
  const linkExpired = new URLSearchParams(search).get('link') === 'expired';

  if (user) return <Redirect to={returnTo} />;

  return (
    <SignInLayout
      heading={<FormattedMessage defaultMessage="Sign in or create an account" />}
      title={intl.formatMessage({ defaultMessage: 'Sign in' })}
    >
      {/* Wait for the session so a signed-in visitor is redirected before seeing the form */}
      {!loading && (
        <React.Fragment>
          {linkExpired && (
            <Typography className={classes.expired} role="status">
              <FormattedMessage
                defaultMessage="That sign-in link has expired or was already used. Enter your email and we'll send a new one."
              />
            </Typography>
          )}
          <BasicForm<SignInFormValues>
            mutate={{
              mutation: SendSignInLinkDocument,
              // The address travels in history state, not the URL, so it stays out of logs and analytics
              onCompleted: (_data, { email = '' }) => history.push(withReturnTo('/sign-in/check-email', returnTo), { email: email.trim() }),
              onError: () => enqueueSnackbar(intl.formatMessage({ defaultMessage: "We couldn't send a link to that email. Check it and try again." }), { variant: 'error' }),
              toVariables: ({ email = '' }) => ({ email: email.trim(), returnTo }),
            }}
            suppressSnackbar
          >
            <Box>
              <TextFieldGroup
                field="email"
                includeTitleInErrorMessage={false}
                isRequiredTitleLabel
                title={intl.formatMessage({ defaultMessage: 'Email' })}
                type="email"
                validate={(value?: string) => (value?.trim() ? false : intl.formatMessage({ defaultMessage: 'Enter your email' }))}
                variant="outlined"
              />
              <SubmitGroup block title={<FormattedMessage defaultMessage="Email me a sign-in link" />} />
              <Typography className={classes.hint} variant="body2">
                <FormattedMessage defaultMessage="We'll email you a link that signs you in. No password needed." />
              </Typography>
            </Box>
          </BasicForm>
          <Box className={classes.divider}>
            <Typography variant="body2">
              <FormattedMessage defaultMessage="or" />
            </Typography>
          </Box>
          <SocialSignInButtons returnTo={returnTo} />
          <Typography className={classes.legal} variant="caption">
            <FormattedMessage
              defaultMessage="By continuing you agree to Popmenu's <privacy>Privacy Policy</privacy>."
              values={{
                privacy: (chunks: React.ReactNode) => <a href="https://my.popmenu.com/privacy" rel="noopener" target="_blank">{chunks}</a>,
              }}
            />
          </Typography>
        </React.Fragment>
      )}
    </SignInLayout>
  );
};
